import { maxValue } from "@foretagsplatsen/core-helpers/array";

/**
 * Intermediary representation of what a report header cell is.
 * This is a node within a tree structure to allow groups to be displayed
 * correctly.
 *
 * When a node has no renderer, it will be "ignored" in the sense that its
 * children will render in place of it, hiding the fact that they belong to a
 * group.
 *
 * @param {string} id - Unique identifier of the cell. This is used to fill up
 *   the `headers` attribute
 * @param {Renderer} [renderer] - Renderer for the cell content. The function
 *   is invoked with an `attributes` object that is used as additional
 *   attributes for the renderable
 * @param {HeaderCell[]} [children] - Cells owned by this node
 */
export default class HeaderCell {
	constructor({ id, renderer, children = [] }) {
		this._id = id;
		this._renderer = renderer;
		this._children = children;

		this._rowspan = 1;
		this._cachedHeight = undefined;
		this._parentAttributeId = undefined;
	}

	asNormalizedArray() {
		if (this._shouldBeIgnored()) {
			return this._children;
		}

		return [this];
	}

	hasChildren() {
		return !!this._children.length;
	}

	getChildren() {
		return this._children;
	}

	/**
	 * Computes and caches the height of the node.
	 * The height is the biggest distance from this node to its leaves.
	 *
	 * @return {number}
	 */
	getHeight() {
		if (this._cachedHeight !== undefined) return this._cachedHeight;

		if (!this._children.length) {
			this._cachedHeight = 0;
			return this._cachedHeight;
		}

		const maxHeight = maxValue(this._children, (child) =>
			child.getHeight(),
		);

		this._cachedHeight = maxHeight + 1;
		return this._cachedHeight;
	}

	/**
	 * This ensures the tree is perfect, at the height provided as argument.
	 * A `perfect` tree is a tree whose branches have the exact same depth.
	 *
	 * Instead of adding children as we would normally do, we instead use the
	 * `rowspan` attribute to "fake" the depth of a node.
	 *
	 * @param {number} [height] - Distance to all the leaves once the tree is
	 *   perfect
	 * @param {string} [parentAttributeId] - Header id of the parent node.
	 *   This will be used to fill up the `headers` attribute
	 */
	perfect({ height = this.getHeight(), parentAttributeId } = {}) {
		/*
		 * The method takes benefit of visiting all the nodes down to set needed
		 * values (depth and parentAttributeId) so that we don't have to keep a
		 * pointer to the parent node to lazily access those values.
		 */

		this._parentAttributeId = parentAttributeId;

		if (!height) return;

		if (this._children.length) {
			for (const child of this._children) {
				child.perfect({
					height: height - 1,
					parentAttributeId: this._getAttributeId(),
				});
			}
		} else {
			this._rowspan = height + 1;
		}
	}

	/**
	 * Invoke the renderer function with our own custom attributes
	 * so that we can append them to the renderable.
	 *
	 * @return {Renderable}
	 */
	getRenderable() {
		const colspan = this._getColspan();
		const rowspan = this._getRowspan();

		return this._renderer({
			attributes: {
				id: this._getAttributeId(),
				headers: this._parentAttributeId,
				colspan,
				rowspan,
				"data-group-header": this.hasChildren(),
			},
		});
	}

	_shouldBeIgnored() {
		return !this._renderer;
	}

	_getColspan() {
		if (!this._children.length) return 1;

		return this._children.reduce(
			(result, child) => result + child._getColspan(),
			0,
		);
	}

	_getRowspan() {
		return this._rowspan;
	}

	_getAttributeId() {
		return `header-cell-${this._id}`;
	}
}

/**
 * Convert a collection of HeaderCell instances into table rows.
 * `title` can be provided as well to inject a title cell in the first row.
 *
 * @param {Renderable} [title] - Title of the report
 * @param {HeaderCell[][]} headerCellsArray - List of lists of HeaderCell
 *   instance to traverse. This is because the visitXXX functions in
 *   ColumnTitleRenderer return arrays
 * @return {Renderable[]}
 */
export function convertHeaderCellsToTableRowsGroup({
	title,
	headerCellsArray,
}) {
	const headerCells = headerCellsArray.flat();
	const result = [];
	if (title) {
		result.push([title]);
	}

	perfectForest(headerCells);

	levelOrderTraversal({
		nodes: headerCells,
		fn: ({ node: headerCell, depth }) => {
			result[depth] ||= title ? [(html) => html.th("")] : [];
			result[depth].push(headerCell.getRenderable());
		},
	});

	return result.map((headers) => (html) => html.tr(headers));
}

/**
 * Make sure the HeaderCell tree is perfect (a `perfect` tree is a tree whose
 * branches have the exact same depth).
 * Instead of adding children as we would normally do, we instead use the
 * `rowspan` attribute to "fake" the depth of a node.
 *
 * @param {HeaderCell[]} headerCells - List of HeaderCell instances that are
 *  the root nodes
 */
function perfectForest(headerCells) {
	const fakeRoot = new HeaderCell({ children: headerCells });
	fakeRoot.perfect();
}

/**
 * Traverse a tree structure following level order, and invoke `fn` on each
 * node.
 *
 * @param {object[]} nodes - Root nodes of a tree
 * @param {function} fn - Function invoked on each tree node
 *   with the node and its depth
 */
function levelOrderTraversal({ nodes, fn }) {
	let depth = -1;
	let queue;
	let nextQueue = [...nodes];

	while (nextQueue.length) {
		queue = nextQueue;
		nextQueue = [];
		depth++;

		for (const node of queue) {
			fn({ node, depth });

			if (node.hasChildren()) {
				nextQueue.push(...node.getChildren());
			}
		}
	}
}
